import { ReactNode } from 'react';

import { FormControlLabel } from '@mui/material';

import {
  Checkbox,
  Icons,
  Tooltip,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

import type { StackProps } from '@cast/design-system';

export type AgentSetupBlockProps = {
  heading: string;
  description: ReactNode;
  icon?: ReactNode;
  labelChip?: ReactNode;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  hasBeenInstalled?: boolean;
  readonly?: boolean;
  readonlyTooltip?: ReactNode;
  testId?: string;
  isNestedBlock?: boolean;
  showCheckbox?: boolean;
  rootProps?: StackProps;
  addon?: ReactNode;
};

export const AgentSetupBlock = ({
  heading,
  description,
  icon,
  labelChip,
  checked,
  indeterminate = false,
  onChange,
  hasBeenInstalled,
  testId,
  readonly,
  readonlyTooltip,
  isNestedBlock = false,
  showCheckbox = true,
  rootProps = {},
  addon,
}: AgentSetupBlockProps) => {
  const renderBlockContent = (): ReactNode => {
    const content = (
      <Stack gap={2}>
        <Stack direction="row" alignItems="center" gap={8}>
          <Typography
            variant={isNestedBlock ? 'P12B' : 'P14B'}
            color={colorOrToken('grey.900', tokens.theme.fg.structural.primary)}
          >
            {heading}
          </Typography>
          {labelChip}
        </Stack>
        <Typography component="div" variant="P12R" color="textSecondary">
          {description}
        </Typography>
        {addon}
      </Stack>
    );

    if (showCheckbox) {
      return (
        <Tooltip
          title={readonlyTooltip}
          placement="top"
          wrapIf={!!readonly && !!readonlyTooltip}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              hasBeenInstalled ? (
                <Tooltip
                  title="Feature is enabled"
                  placement="top-start"
                  sx={{
                    marginLeft: -12,
                  }}
                >
                  <Box
                    alignSelf="flex-start"
                    minWidth={32}
                    data-testid={`${testId}-mark`}
                  >
                    <Icons.Check size={20} weight="bold" />
                  </Box>
                </Tooltip>
              ) : (
                <Checkbox
                  size={isNestedBlock ? 'small' : 'medium'}
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={(_, checked) => onChange?.(checked)}
                  testId={`${testId}-checkbox`}
                  sx={{ alignSelf: 'flex-start', pr: isNestedBlock ? 10 : 12 }}
                />
              )
            }
            label={content}
            disabled={hasBeenInstalled || readonly}
          />
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      gap={12}
      py={isNestedBlock ? 8 : 12}
      borderTop={
        !isNestedBlock
          ? colorOrToken('grey.100', tokens.theme.border.structural.neutral)
          : undefined
      }
      data-testid={testId}
      {...rootProps}
    >
      {renderBlockContent()}
      {!!icon && (
        <Box
          sx={{
            svg: {
              width: 36,
              height: 36,
            },
          }}
        >
          {icon}
        </Box>
      )}
    </Stack>
  );
};
